// Extra item columns for the variance report, pulled from the client's book
// stock file and AMD (article master) file: category, sub-category, class,
// size, colour, brand, rates... Files are read in this browser only.

import { detectDelimiter, iterateRows, normaliseHeader, parseRate } from '../../reports/engine';
import { readPreview } from '../../components/MasterFileUploader';

/**
 * Reads a file into { sheets: [{ name }], readAll(sheetIndex) -> rows }.
 * Text files: the delimiter is the one most of the first 20 lines use, so a
 * title line above the header does not break the columns.
 */
export async function readSourceFile(file) {
  if (/\.(xlsx|xlsb|xls)$/i.test(file.name)) return readPreview(file);
  const text = (await file.text()).replace(/^\ufeff/, '');
  const votes = new Map();
  text
    .split(/\r?\n/)
    .filter((line) => line.trim())
    .slice(0, 20)
    .forEach((line) => {
      const d = detectDelimiter(line);
      votes.set(d, (votes.get(d) || 0) + 1);
    });
  const delimiter = [...votes.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || '\t';
  const rows = [...iterateRows(text, delimiter)].map((row) => row.cells.map((cell) => String(cell ?? '')));
  return { kind: 'text', sheets: [{ name: file.name }], readAll: async () => rows };
}

/** Report columns a file can fill. */
export const PULL_FIELDS = [
  { key: 'article', label: 'Article', aliases: ['article', 'article code', 'article no', 'article number', 'sku', 'item code', 'material', 'material code', 'internal code', 'style code'] },
  { key: 'description', label: 'Description', aliases: ['description', 'item description', 'article description', 'product name', 'product', 'item name', 'material description', 'desc'] },
  { key: 'category', label: 'Category', aliases: ['category', 'department', 'dept', 'division', 'mc', 'merchandise category', 'category name'] },
  { key: 'sub_category', label: 'Sub-Category', aliases: ['sub category', 'subcategory', 'sub cat', 'sub department', 'sub dept', 'sub category name'] },
  { key: 'class', label: 'Class', aliases: ['class', 'class name', 'section'] },
  { key: 'sub_class', label: 'Sub-class', aliases: ['sub class', 'subclass', 'sub class name'] },
  { key: 'size', label: 'Size', aliases: ['size', 'sz', 'size code'] },
  { key: 'color', label: 'Color', aliases: ['color', 'colour', 'shade', 'color code', 'colour code'] },
  { key: 'brand', label: 'Brand', aliases: ['brand', 'brand name', 'label'] },
  { key: 'mrp', label: 'MRP (SP)', rate: true, aliases: ['mrp', 'mrp price', 'selling price', 'sp', 'retail price', 'rsp'] },
  { key: 'cost', label: 'Cost (CP)', rate: true, aliases: ['cost', 'cost price', 'cp', 'purchase price', 'landed cost', 'unit cost', 'wac'] },
];

const EAN_ALIASES = new Set(['ean', 'barcode', 'bar code', 'ean code', 'upc', 'gtin', 'ean no', 'ean number', 'ean 13', 'ean13']);

/** Best guess of each column from a header row. */
export function guessMapping(headers) {
  const norm = headers.map((h) => normaliseHeader(h));
  const used = new Set();
  const find = (aliases) => {
    const index = norm.findIndex((h, i) => !used.has(i) && aliases.has(h));
    if (index >= 0) used.add(index);
    return index >= 0 ? String(index) : '';
  };
  const mapping = { ean: find(EAN_ALIASES) };
  PULL_FIELDS.forEach((field) => {
    mapping[field.key] = find(new Set(field.aliases));
  });
  return mapping;
}

/** First row (of the first 20) that looks like a header: EAN or article plus one more known column. */
export function guessHeaderRow(rows) {
  let best = { row: 0, score: 0 };
  rows.slice(0, 20).forEach((row, index) => {
    const m = guessMapping(row || []);
    const score = Object.values(m).filter((v) => v !== '').length + (m.ean !== '' ? 2 : 0) + (m.article !== '' ? 1 : 0);
    if (score > best.score) best = { row: index, score };
  });
  return best.row;
}

const clean = (value) => String(value ?? '').replace(/[\r\n\t]+/g, ' ').trim();
const keyOf = (value) => clean(value).toUpperCase();
/** EAN as text without leading zeros, so 0890… and 890… match. */
const eanKey = (value) => keyOf(value).replace(/^0+(?=\d)/, '');

/**
 * Builds the lookup for one file. rows = every row of the chosen sheet
 * (arrays of cell text); mapping = { ean, article, category, ... } as column
 * indexes ('' = not used).
 */
export function buildLookup(rows, headerRow, mapping) {
  const byEan = new Map();
  const byArticle = new Map();
  const fields = PULL_FIELDS.filter((f) => mapping[f.key] !== '' && mapping[f.key] !== undefined);
  const eanCol = mapping.ean === '' ? -1 : Number(mapping.ean);
  const articleCol = mapping.article === '' || mapping.article === undefined ? -1 : Number(mapping.article);
  let count = 0;
  for (const row of rows.slice(headerRow + 1)) {
    if (!row) continue;
    const record = {};
    fields.forEach((f) => {
      const raw = row[Number(mapping[f.key])];
      if (f.rate) {
        const rate = parseRate(raw);
        if (rate !== null) record[f.key] = rate;
      } else {
        const text = clean(raw);
        if (text) record[f.key] = text;
      }
    });
    const ean = eanCol >= 0 ? eanKey(row[eanCol]) : '';
    const article = articleCol >= 0 ? keyOf(row[articleCol]) : '';
    if (!ean && !article) continue;
    count += 1;
    // First row wins for an EAN; for an article, rows fill each other's blanks.
    if (ean && !byEan.has(ean)) byEan.set(ean, record);
    if (article) byArticle.set(article, { ...record, ...(byArticle.get(article) || {}) });
  }
  return { byEan, byArticle, rows: count, fields: fields.map((f) => f.key) };
}

/** Values for one report item from one file (by EAN, else by article). */
export function lookupItem(lookup, item) {
  if (!lookup) return null;
  const ean = eanKey(item.ean);
  return (
    lookup.byEan.get(ean) ||
    (item.article ? lookup.byArticle.get(keyOf(item.article)) : null) ||
    (item.by_article ? lookup.byArticle.get(keyOf(item.ean)) : null) ||
    null
  );
}

/**
 * item -> extra values. Book stock file first, then the AMD file; the
 * report keeps the portal's own values and uses these for blanks and the
 * extra columns.
 */
export function combineLookups(lookups) {
  const list = lookups.filter(Boolean);
  if (!list.length) return null;
  return (item) => {
    const out = {};
    list.forEach((lookup) => {
      const found = lookupItem(lookup, item);
      if (!found) return;
      Object.entries(found).forEach(([key, value]) => {
        if (out[key] === undefined || out[key] === '') out[key] = value;
      });
    });
    return out;
  };
}

/** How many report items a lookup covers. */
export function coverage(lookup, items) {
  if (!lookup) return 0;
  return items.reduce((n, item) => n + (lookupItem(lookup, item) ? 1 : 0), 0);
}

/** Remembered column choices per file kind (by header text). */
const STORE_KEY = 'varianceReportColumns';
export function rememberMapping(kind, headers, mapping) {
  try {
    const saved = JSON.parse(localStorage.getItem(STORE_KEY) || '{}');
    saved[kind] = Object.fromEntries(
      Object.entries(mapping)
        .filter(([, v]) => v !== '')
        .map(([k, v]) => [k, normaliseHeader(headers[Number(v)] || '')]),
    );
    localStorage.setItem(STORE_KEY, JSON.stringify(saved));
  } catch {
    // storage unavailable: choices last until the page is closed
  }
}

export function recallMapping(kind, headers, guess) {
  try {
    const saved = JSON.parse(localStorage.getItem(STORE_KEY) || '{}')[kind];
    if (!saved) return guess;
    const norm = headers.map((h) => normaliseHeader(h));
    const out = { ...guess };
    Object.entries(saved).forEach(([key, header]) => {
      const index = norm.indexOf(header);
      if (index >= 0) out[key] = String(index);
    });
    return out;
  } catch {
    return guess;
  }
}
